/* Admin · Community Layer 3 Marketplace overview.
 *
 * Cross-workspace view of every buy-box, the most-active cash buyers,
 * the newest boxes, and the leads pulling the most matches right now.
 *
 * Endpoint: GET /api/admin/marketplace/stats
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, RefreshCw, Target, Users, MapPin, TrendingUp, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, fmtMoney } from "@/lib/api";

export default function AdminMarketplace() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/marketplace/stats");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load marketplace stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="animate-spin text-indigo-600" />
      </div>
    );
  }

  const stats = data || {};

  return (
    <div className="flex flex-col gap-6" data-testid="admin-marketplace">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-extrabold text-stone-900 leading-tight flex items-center gap-2">
            <Target size={20} className="text-indigo-600" /> Marketplace · Community Layer 3
          </h1>
          <p className="text-stone-500 text-sm mt-0.5">
            Two-sided buyer/seller matching across every workspace. Buy-boxes are the standing intent.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="btn-outline flex items-center gap-1.5"
          data-testid="admin-marketplace-refresh"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Health strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-marketplace-stats">
        <StatTile icon={Target} label="Total buy-boxes" value={stats.total_boxes || 0} accent="#6366F1" />
        <StatTile icon={Sparkles} label="Active boxes" value={stats.active_boxes || 0} accent="#10B981" />
        <StatTile icon={Users} label="Cash buyers w/ boxes" value={stats.distinct_buyers || 0} accent="#F59E0B" />
        <StatTile icon={TrendingUp} label="Wholesalers" value={stats.distinct_wholesalers || 0} accent="#EC4899" />
      </div>

      {/* Top buyers */}
      <div className="card p-5" data-testid="admin-marketplace-top-buyers">
        <div className="font-display font-bold text-lg mb-3 flex items-center gap-2">
          <Users size={16} className="text-amber-600" /> Top Cash Buyers by active buy-boxes
        </div>
        {(stats.top_buyers || []).length === 0 ? (
          <div className="text-sm text-[var(--ink-3)] text-center py-6">
            No active buy-boxes yet across the platform.
          </div>
        ) : (
          <div className="space-y-1.5">
            {stats.top_buyers.map((b, i) => (
              <div
                key={b.buyer_id}
                className="flex items-center gap-3 p-2.5 rounded-lg border border-[var(--line)] hover:bg-slate-50"
                data-testid={`admin-top-buyer-${i}`}
              >
                <div className="shrink-0 w-7 h-7 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-xs">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{b.buyer_name}</div>
                  <div className="text-[11px] text-[var(--ink-3)]">User: {b.user_id?.slice(0, 8) || "—"}</div>
                </div>
                <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-full bg-indigo-100 text-indigo-700">
                  <Target size={10} /> {b.boxes} box{b.boxes === 1 ? "" : "es"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Hot leads */}
      <div className="card p-5" data-testid="admin-marketplace-hot-leads">
        <div className="font-display font-bold text-lg mb-3 flex items-center gap-2">
          <TrendingUp size={16} className="text-rose-600" /> Most-saleable leads (network-wide)
        </div>
        <div className="text-xs text-[var(--ink-3)] mb-3">
          Leads with the most buy-boxes matching across all workspaces — the inventory the network wants most.
        </div>
        {(stats.hot_leads || []).length === 0 ? (
          <div className="text-sm text-[var(--ink-3)] text-center py-6">
            No leads currently match any active buy-boxes.
          </div>
        ) : (
          <div className="space-y-1.5">
            {stats.hot_leads.map((lead) => (
              <Link
                key={lead.id}
                to={`/app/leads/${lead.id}/report`}
                className="flex items-center gap-3 p-2.5 rounded-lg border border-[var(--line)] hover:bg-rose-50 hover:border-rose-300 transition-colors"
                data-testid={`admin-hot-lead-${lead.id}`}
              >
                <MapPin size={14} className="text-[var(--ink-3)] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{lead.property_address}</div>
                  <div className="text-[11px] text-[var(--ink-3)] truncate">
                    {[lead.city, lead.state].filter(Boolean).join(", ")}
                    {lead.estimated_arv ? ` · ARV ${fmtMoney(lead.estimated_arv)}` : ""}
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-full bg-rose-100 text-rose-700">
                  <Sparkles size={10} /> {lead._match_count} matches
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Recent boxes */}
      <div className="card p-5" data-testid="admin-marketplace-recent-boxes">
        <div className="font-display font-bold text-lg mb-3 flex items-center gap-2">
          <Target size={16} className="text-indigo-600" /> Newest buy-boxes
        </div>
        {(stats.recent_boxes || []).length === 0 ? (
          <div className="text-sm text-[var(--ink-3)] text-center py-6">
            No buy-boxes created yet.
          </div>
        ) : (
          <div className="space-y-1.5">
            {stats.recent_boxes.map((b) => (
              <div
                key={b.id}
                className="flex items-center gap-3 p-2.5 rounded-lg border border-[var(--line)]"
                data-testid={`admin-recent-box-${b.id}`}
              >
                <Target size={13} className={b.active ? "text-indigo-600" : "text-slate-400"} />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">
                    {b.name} · <span className="font-normal text-[var(--ink-3)]">{b.buyer_name || "—"}</span>
                  </div>
                  <div className="text-[11px] text-[var(--ink-3)]">
                    {(b.states || []).join(", ") || "—"}
                    {(b.min_price != null || b.max_price != null) && (
                      <>
                        {" · "}
                        {b.min_price != null ? fmtMoney(b.min_price) : "$0"} – {b.max_price != null ? fmtMoney(b.max_price) : "any"}
                      </>
                    )}
                  </div>
                </div>
                {!b.active && (
                  <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500">paused</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, accent }) {
  return (
    <div className="card p-4" data-testid={`stat-tile-${label.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white shrink-0" style={{ background: accent }}>
          <Icon size={18} />
        </div>
        <div>
          <div className="label-eyebrow">{label}</div>
          <div className="font-display font-extrabold text-2xl tabular-nums mt-0.5">{value.toLocaleString()}</div>
        </div>
      </div>
    </div>
  );
}
